import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { useClassName } from 'common/hooks';
import Asset from './Asset';
import Text from './Text';
import CTA from './CTA';
import chevronRight from 'common/assets/chevron-right.svg';

import './gallery.less';

const getContainerSize = (totalImages) => {
  if (totalImages === 1) return 'large';
  if (totalImages === 2) return 'medium';
  if (totalImages === 3 || (totalImages % 3 === 0 && totalImages % 4 !== 0))
    return 'small';
  return 'tiny';
};

const Gallery = ({
  images,
  isCardGallery,
  isVerticalVideosGallery,
  handleTrack,
}) => {
  const history = useHistory();
  const className = useClassName('ModularGallery');
  const imageExtraClasses = [];
  const contentExtraClasses = [];
  const overlayExtraClasses = [];
  const linkCTAExtraClasses = [];

  if (isVerticalVideosGallery) {
    imageExtraClasses.push('vertical-video-gallery');
    contentExtraClasses.push('content-vertical-video-gallery');
  }
  if (isCardGallery) {
    contentExtraClasses.push('content-card');
    imageExtraClasses.push('image-container-card');
  }

  const size = getContainerSize(images.length);
  contentExtraClasses.push(`content-${size}`);
  if (!isVerticalVideosGallery) {
    imageExtraClasses.push(`image-container-${size}`);
  }

  if (images.length <= 2) {
    overlayExtraClasses.push('overlay-big');
    linkCTAExtraClasses.push('link-cta-rounded');
  }

  const openURL = (url) => {
    const isExternal = url.startsWith('http');
    if (isExternal) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  };

  const renderTextBlock = (textBlock) => {
    return (
      <div
        className={className([
          'text-block',
          `text-block-${textBlock.textAlign}`,
          `text-block-${
            textBlock.backgroundColor ? 'extra' : 'regular'
          }-padding`,
        ])}>
        {textBlock.overline && (
          <Text key="overline" text={textBlock.overline} />
        )}
        {textBlock.title && <Text key="title" text={textBlock.title} />}
        {textBlock.body && <Text key="body" text={textBlock.body} />}
        {textBlock?.cta?.length && (
          <div className={className('text-block-cta-container')}>
            {textBlock.cta.map((cta, index) => (
              <CTA key={`cta-${index}`} cta={cta} handleTrack={handleTrack} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderOverlay = (textBlock) => {
    return (
      <div
        className={className(['overlay', ...overlayExtraClasses])}
        onClick={() => textBlock?.cta?.value && openURL(textBlock.cta.value)}>
        <Text key="title" text={textBlock.title} />
        {textBlock?.cta?.length && (
          <div className={className('cta-container')}>
            {textBlock.cta.map((cta, index) => (
              <div
                key={`cta-${index}`}
                className={className(['link-cta', ...linkCTAExtraClasses])}>
                <img
                  src={chevronRight}
                  alt={'chevron-right'}
                  width={24}
                  height={24}
                  onClick={() => openURL(cta.value)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={className('container')}>
      {images.map((image, index) => (
        <div
          className={className(['content', ...contentExtraClasses])}
          key={`content-${index}`}
          style={{
            backgroundColor: image.textBlock?.backgroundColor
              ? image.textBlock.backgroundColor
              : '',
          }}>
          <div className={className(['image-container', ...imageExtraClasses])}>
            <Asset asset={image} />
            {image.textBlock?.title &&
              isCardGallery &&
              renderOverlay(image.textBlock)}
          </div>
          {image.textBlock &&
            !isCardGallery &&
            renderTextBlock(image.textBlock)}
        </div>
      ))}
    </div>
  );
};

Gallery.propTypes = {
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  isCardGallery: PropTypes.bool,
  isVerticalVideosGallery: PropTypes.bool,
  handleTrack: PropTypes.func,
};

Gallery.defaultProps = {
  isCardGallery: false,
  isVerticalVideosGallery: false,
};

export default Gallery;
